// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { Course, Lesson, Unit } from '.';
import { LessonPart } from './lesson-part';
import { PromptBuilder } from './prompt-builder';

/**
 * Creates a Copilot prompt for generating a course title.
 * Prompt response is expected to be a string.
 */
export function courseTitlePrompt<TLocation = string>(node: Course<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('course')
        .text('Please generate a clear, recognizable and appropriate title for the course.')
        .respondWith('title')
        .beginContext()
        .course(node, { omitTitle: true })
        .unitList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a course description.
 * Prompt response is expected to be a markdown string.
 */
export function courseDescriptionPrompt<TLocation = string>(node: Course<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('course')
        .text(
            'Please generate a short but clear and informative description for the course.',
            'Do not include course title, objectives, prerequisites or list of units in the description.'
        )
        .respondWith('description')
        .beginContext()
        .course(node, { omitDescription: true })
        .unitList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating course objectives.
 * Prompt response is expected to be a markdown string.
 */
export function courseObjectivesPrompt<TLocation = string>(node: Course<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('course')
        .text(
            "Please generate a bulleted list of objectives for the course using Bloom's verbs.",
            'Prefix the list with the phrase "In this course, you will learn to:".'
        )
        .respondWith('description')
        .beginContext()
        .course(node, { omitObjectives: true })
        .unitList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating course prerequisites.
 * Prompt response is expected to be a markdown string.
 */
export function coursePrerequisitesPrompt<TLocation = string>(node: Course<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('course')
        .text(
            'Please generate a bulleted list of prerequisites for the course.',
            'Do not prefix the list with any text.',
            'Start with the following two items:',
            '- A school or company issued email account',
            '- Access to a device (computer or tablet) with an internet connection'
        )
        .respondWith('description')
        .beginContext()
        .course(node, { omitPrerequisites: true })
        .unitList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a unit title.
 * Prompt response is expected to be a string.
 */
export function unitTitlePrompt<TLocation = string>(node: Unit<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('unit')
        .text(
            'Please generate a clear, recognizable and appropriate title for the unit.',
            'Assume the unit title will be listed immediately after the course title.'
        )
        .respondWith('title')
        .beginContext()
        .unit(node, { omitTitle: true })
        .lessonList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a unit description.
 * Prompt response is expected to be a markdown string.
 */
export function unitDescriptionPrompt<TLocation = string>(node: Unit<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('unit')
        .text('Please generate a short but clear and informative description for the unit.')
        .respondWith('description')
        .beginContext()
        .unit(node, { omitDescription: true })
        .lessonList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating unit objectives.
 * Prompt response is expected to be a markdown string.
 */
export function unitObjectivesPrompt<TLocation = string>(node: Unit<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('unit')
        .text(
            "Please generate a bulleted list of objectives for the unit using Bloom's verbs.",
            'Prefix the list with the phrase "In this unit, you will learn to:".'
        )
        .respondWith('description')
        .beginContext()
        .unit(node, { omitObjectives: true })
        .lessonList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating lesson title.
 * Prompt response is expected to be a markdown string.
 */
export function lessonTitlePrompt<TLocation = string>(node: Lesson<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('lesson')
        .text(
            'Please generate a short title for the lesson.',
            'Assume the lesson title will be listed immediately after the course and unit titles.'
        )
        .respondWith('title')
        .beginContext()
        .lesson(node, { omitTitle: true })
        .lessonSiblings(node)
        .pageList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a lesson description.
 * Prompt response is expected to be a markdown string.
 */
export function lessonDescriptionPrompt<TLocation = string>(node: Lesson<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('lesson')
        .text('Please generate a short but clear and informative description for the lesson.')
        .respondWith('description')
        .beginContext()
        .lesson(node, { omitDescription: true })
        .lessonSiblings(node)
        .pageList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating lesson objectives.
 * Prompt response is expected to be a markdown string.
 */
export function lessonObjectivesPrompt<TLocation = string>(node: Lesson<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('lesson')
        .text(
            "Please generate a bulleted list of objectives for the lesson using Bloom's verbs.",
            'Prefix the list with the phrase "Upon completion of this lesson, you\'ll be able to:".'
        )
        .respondWith('description')
        .beginContext()
        .lesson(node, { omitObjectives: true })
        .lessonSiblings(node)
        .pageList(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating lesson page title.
 * Prompt response is expected to be a markdown string.
 */
export function pageTitlePrompt<TLocation = string>(node: LessonPart<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate a short title for the lesson page.',
            'Assume the page title will be listed immediately after the course, unit and lesson titles.'
        )
        .respondWith('title')
        .beginContext()
        .lesson(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a page duration.
 * Prompt response is expected to be an integer.
 */
export function pageDurationPrompt<TLocation = string>(node: LessonPart<TLocation>): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please estimate the number of minutes needed by a high school or college student to read and understand the content of the page and complete exercises, if present.'
        )
        .respondWith('duration')
        .beginContext()
        .lesson(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a new page outline.
 * Prompt response is expected to be a markdown string.
 */
export function pageOutlinePrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate an outline for a new lesson page that will be no longer than 1-2 average computer screens once complete.',
            'Do not add page title header in the markdown.',
            'Include section headers and short description of content, but not the actual content.'
        )
        .value('The teacher has requested the following outline', userPrompt)
        .respondWith('content')
        .beginContext()
        .lesson(node)
        .pageSiblings(node)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a new page content.
 * Prompt response is expected to be a markdown string.
 */
export function pageContentPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate content for a new lesson page that is no longer than 1-2 average computer screens.',
            'Do not add page title header to the markdown.'
        )
        .value('The teacher has requested the following content', userPrompt)
        .respondWith('content')
        .beginContext()
        .lesson(node)
        .pageSiblings(node)
        .toString();
}

/**
 * Creates a Copilot prompt for rewriting page content.
 * Prompt response is expected to be a markdown string.
 */
export function pageRewritePrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text('Please update the lesson page content as requested by the teacher.')
        .value('The teacher has requested the following changes to the content', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for expanding page content.
 * Prompt response is expected to be a markdown string.
 */
export function pageExpandContentPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt?: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text('Please expand the page content on the same topic.')
        .value('The teacher has provided the following additional instructions', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for make page content more concise.
 * Prompt response is expected to be a markdown string.
 */
export function pageReduceContentPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt?: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text('Please make the page content more concise.')
        .value('The teacher has provided the following additional instructions', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for changing page content reading level.
 * Prompt response is expected to be a markdown string.
 */
export function readingLevelPrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    level: 'middle-school' | 'high-school' | 'college',
    userPrompt?: string
): string {
    let audience: string;
    switch (level) {
        case 'middle-school':
            audience = 'middle school student';
            break;
        case 'high-school':
            audience = 'high school student';
            break;
        case 'college':
            audience = 'college student';
            break;
    }

    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(`Please update the page content to make it appropriate for a ${audience}.`)
        .value('The teacher has provided the following additional instructions', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating paragraph content.
 * Prompt response is expected to be a markdown string.
 */
export function paragraphPrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    cursor: number,
    userPrompt: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please update the page content to add a new paragraph after the cursor position as requested by the teacher.',
            'Keep the rest of the content unchanged.'
        )
        .useCursor()
        .value('Description of the new paragraph requested by the teacher', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content, cursor)
        .toString();
}

/**
 * Creates a Copilot prompt for rewriting paragraph content.
 * Prompt response is expected to be a markdown string.
 */
export function paragraphRewritePrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    cursor: number,
    userPrompt: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please update the page content to rewrite the paragraph at the cursor position as requested by the teacher.',
            'Keep the rest of the content unchanged.'
        )
        .useCursor()
        .value('The teacher has requested the following changes to the paragraph', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content, cursor)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a code example.
 * Prompt response is expected to be a markdown string.
 */
export function codeExamplePrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    cursor: number,
    userPrompt: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please update the page content to add a new code example after the paragraph at the cursor position as requested by the teacher.',
            'Keep the rest of the content unchanged.'
        )
        .useCursor()
        .value('Description of the new code example requested by the teacher', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content, cursor)
        .toString();
}

/**
 * Creates a Copilot prompt for rewriting a code example.
 * Prompt response is expected to be a markdown string.
 */
export function codeRewritePrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    cursor: number,
    userPrompt: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please update the page content to change the code example at the cursor position as requested by the teacher.',
            'Keep the rest of the content unchanged.'
        )
        .useCursor()
        .value('The teacher has requested the following changes to the code example', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content, cursor)
        .toString();
}

/**
 * Creates a Copilot prompt for generating external source summary.
 * Prompt response is expected to be a markdown string.
 */
export function externalSummaryPrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    cursor: number,
    userPrompt: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please update the page content to add a paragraph after the paragraph at the cursor location summarizing the external source indicated by the teacher.',
            'If possible, include the source URL as attribution in the paragraph.',
            'Keep the rest of the content unchanged.'
        )
        .useCursor()
        .value('The external source the teacher has requested to summarize', userPrompt)
        .respondWith('content')
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .pageContent(node.content, cursor)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a string question.
 * Prompt response is expected to be a JSON for the question.
 */
export function stringQuestionPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt?: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate a new short answer question prompt for the student based on page content:',
            '{',
            '  "content": "question statement",',
            '  "options": [',
            '    {',
            '      "value": "correct answer",',
            '      "isCorrect": true,',
            '      "explanation": "correct answer explanation"',
            '    }',
            '  ]',
            '}',
            'The "content" and "explanation" should be both single sentences.',
            'The "value" should be a single word or short phrase.',
            '"explanation" is shown to the student if they type the correct answer.'
        )
        .respondWith('json')
        .value('The teacher has requested the following question', userPrompt)
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .questionSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a true/false question.
 * Prompt response is expected to be a JSON for the question.
 */
export function choiceQuestionPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt?: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate a new single choice question for the student based on page content:',
            '{',
            '  "content": "question statement",',
            '  "options": [',
            '    {',
            '     "value": "choice text",',
            '     "isCorrect": true,',
            '     "explanation": "correct answer explanation"',
            '   },',
            '   {',
            '     "value": "choice text",',
            '     "isCorrect": false,',
            '     "explanation": "incorrect answer explanation"',
            '   },',
            '   ...',
            '  ]',
            '}',
            'The question statement should be a single sentence.',
            'Exactly one of the options should have "isCorrect" set to true, others should have "isCorrect" set to false.',
            '"explanation" should be a single sentence each explaining their respective options.'
        )
        .respondWith('json')
        .value('The teacher has requested the following question', userPrompt)
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .questionSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a multiple choice question.
 * Prompt response is expected to be a JSON for the question.
 */
export function multipleChoiceQuestionPrompt<TLocation = string>(
    node: LessonPart<TLocation>,
    userPrompt?: string
): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate a new multiple choice question for the student based on page content:',
            '{',
            '  "content": "question statement",',
            '  "options": [',
            '   {',
            '     "value": "choice text",',
            '     "isCorrect": true,',
            '     "explanation": "correct answer explanation"',
            '   },',
            '   {',
            '     "value": "choice text",',
            '     "isCorrect": false,',
            '     "explanation": "incorrect answer explanation"',
            '   },',
            '   ...',
            '  ]',
            '}',
            'The question statement should be a single sentence.',
            'There should be at least one option with isCorrect set to true.',
            'There should be one or more incorrect options with isCorrect set to false.',
            '"explanation" should be a single sentence each, it will be shown when the corresponding option is selected.'
        )
        .respondWith('json')
        .value('The teacher has requested the following question', userPrompt)
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .questionSiblings(node)
        .pageContent(node.content)
        .toString();
}

/**
 * Creates a Copilot prompt for generating a reflection question.
 * Prompt response is expected to be a string.
 */
export function reflectionQuestionPrompt<TLocation = string>(node: LessonPart<TLocation>, userPrompt?: string): string {
    return new PromptBuilder<TLocation>()
        .useContext('lessonpart')
        .text(
            'Please generate a new reflection question prompt for the student based on page content.',
            '{',
            '  "content": "question prompt",',
            '}'
        )
        .respondWith('json')
        .value('The teacher has requested the following question', userPrompt)
        .beginContext()
        .page(node)
        .pageSiblings(node)
        .questionSiblings(node)
        .pageContent(node.content)
        .toString();
}

// eslint-disable-next-line security/detect-non-literal-regexp
const cursorRegex = new RegExp(PromptBuilder.CursorToken, 'g');

/**
 * Extracts markdown text from the Copilot response.
 * Returns the original text if the response is not a valid markdown.
 */
export function extractMarkdownResponse(text: string): string {
    const result = text.match(/^\s*```(?:markdown)?\n*(.*)```\s*$/s)?.[1] ?? text;
    return result.replace(cursorRegex, '');
}

/**
 * Extracts JSON object from the Copilot response.
 * Returns undefined if the response is not a valid JSON.
 */
export function extractJsonResponse(text: string): object | undefined {
    const json = text.match(/^\s*```(?:json)?\n*(.*)```\s*$/s)?.[1] ?? text;
    try {
        return JSON.parse(json);
    } catch {
        return undefined;
    }
}
